import { Directive, ElementRef, Inject, Input, Renderer2 } from '@angular/core';
import { DOCUMENT } from '@angular/common';

@Directive({
  selector: '[shimmer]',
})
export class ShimmerDirective {
  private static styleTagId = 'shimmer-directive-styles-v1';
  private overlayEl: HTMLElement | null = null;

  @Input('shimmer') set shimmer(value: any) {
    const isLoading = value === undefined || value === null;

    if (isLoading) this.show();
    else this.hide();
  }

  constructor(
    private el: ElementRef<HTMLElement>,
    private renderer: Renderer2,
    @Inject(DOCUMENT) private document: Document
  ) {
    this.injectStylesOnce();
  }

  private injectStylesOnce(): void {
    // SSR-safe: document exists via injection in browser, but still check defensively
    if (!this.document?.head) return;

    // Do not inject twice
    if (this.document.getElementById(ShimmerDirective.styleTagId)) return;

    const styleEl = this.renderer.createElement('style');
    this.renderer.setAttribute(styleEl, 'id', ShimmerDirective.styleTagId);

    const css = `
      .shimmer-container {
        position: relative;
        overflow: hidden;
        min-height: 23rem;
      }

      .shimmer-overlay {
        background-color: white;
        position: absolute;
        inset: 0;
      }

      .shimmer-overlay::after {
        content: "";
        position: absolute;
        top: -50%;
        bottom: -50%;
        left: 0;
        right: 0;
        margin: auto;
        transform: translateX(100%) rotate(20deg);
        background: linear-gradient(90deg,
          rgba(255, 255, 255, 0) 0%,
          rgba(236, 235, 235, 0.4) 20%,
          rgba(240, 240, 240, 0.6) 50%,
          rgba(244, 244, 244, 0.4) 80%,
          rgba(255, 255, 255, 0) 100%
        );
        animation: sweep 1.6s ease-in-out infinite;
        pointer-events: none;
      }

      @keyframes sweep {
        from { transform: translateX(-100%) rotate(20deg); }
        to   { transform: translateX(100%) rotate(20deg); }
      }
    `;

    this.renderer.appendChild(styleEl, this.renderer.createText(css));
    this.renderer.appendChild(this.document.head, styleEl);
  }

  private show(): void {
    const host = this.el.nativeElement;

    this.renderer.addClass(host, 'shimmer-container');

    // Prevent stacking overlays
    if (this.overlayEl) return;

    this.overlayEl = this.renderer.createElement('div');
    this.renderer.addClass(this.overlayEl, 'shimmer-overlay');
    this.renderer.appendChild(host, this.overlayEl);
  }

  private hide(): void {
    const host = this.el.nativeElement;

    this.renderer.removeClass(host, 'shimmer-container');

    if (this.overlayEl) {
      this.renderer.removeChild(host, this.overlayEl);
      this.overlayEl = null;
    }
  }
}
